import { gql, useQuery } from "@apollo/client";
import classNames from "classnames";
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IMenuQuery } from "@repo/shopify/queries/others-gql";
import { menuQuery } from "@repo/shopify/queries/others-gql";
import type { IProductDetailQuery } from "@repo/shopify/queries/product-gql";
import { getTotalProductCount } from "@repo/shopify/utils/collections";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import Filter from "@molecules/Filter";
import ProductCard from "@molecules/ProductCard";

import { NOTIFICATIONS } from "@const/notifications";

import useCollections from "@hooks/useCollections";
import useHeaderScroll from "@hooks/useHeaderScroll";
import useLanguage from "@hooks/useLanguage";

import { useLayoutStore } from "@store/layout";

export default function Collections() {
  const { pathname, search } = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const { isPC } = useCheckDevice();

  useHeaderScroll();

  const { collectionData, products, hasNextPage, loading, handleFetchMore, productCount } = useCollections();

  const { domain, shopifyDomain } = useLanguage();

  const { openToast, showFooter } = useLayoutStore();

  const [showFilter, setShowFilter] = useState<boolean>(isPC && pathname.includes("archive"));

  // 컬렉션 이동 / 필터·정렬 변경 시에만 맨 위로. products를 의존성에 두면 매 렌더마다 스크롤이 튄다
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);

  useEffect(() => {
    showFooter(!hasNextPage);
  }, [hasNextPage]);

  useEffect(() => {
    if (collectionData?.collection.title !== "Daniel Johnston") return;

    document.body.setAttribute("class", "dark");
    return () => {
      document.body.setAttribute("class", "white");
    };
  }, [collectionData?.collection.title]);

  const { data: menuData } = useQuery<IMenuQuery>(gql(menuQuery.query), {
    variables: { handle: "navigation" },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    setShowFilter(isPC && pathname.includes("archive"));
  }, [isPC, pathname]);

  const totalProducts = useMemo(() => getTotalProductCount(collectionData?.collection.products.filters || []), [collectionData]);

  return (
    <>
      {!collectionData && loading ? (
        <Loading type="page" />
      ) : (
        <>
          {isPC ? (
            <>
              <section
                className={classNames(
                  "pc:col-span-2 pc:gap-5 pc:w-screen-col-span-2 top-30 left-5 col-span-8 flex max-h-dvh gap-2 self-start overflow-y-auto pb-40",
                  hasNextPage ? "fixed" : "sticky",
                )}
              >
                {showFilter ? (
                  <Filter
                    filters={collectionData?.collection.products.filters || []}
                    onClickClose={() => {
                      setShowFilter(false);
                    }}
                  />
                ) : (
                  <div className="flex flex-col gap-10">
                    <Button
                      as="text"
                      onClick={() => {
                        setShowFilter(!showFilter);
                      }}
                    >
                      {`Filter & Sort (${totalProducts})`}
                    </Button>
                    <div className="flex flex-col gap-2">
                      {menuData?.menu?.items.map((item) => (
                        <React.Fragment key={item.id}>
                          {item.title !== "Archives" && (
                            <div className="tnt-gap-x pc:col-start-1 col-span-8 col-start-2 flex">
                              <Link
                                as="text"
                                className={classNames(
                                  "pc:col-span-1 col-span-5 w-fit leading-none",
                                  item.title === "Archives" && "pc:pt-2.5 pt-3.5",
                                  !isPC && "text-base!",
                                )}
                                href={`/${item.url?.replace(domain, "").replace(shopifyDomain, "")}`}
                              >
                                {`${item.title}${item.title === "Archives" ? ":" : ""}`}
                              </Link>
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}
              </section>
              <section className="pc:col-span-10 pc:grid-cols-5 pc:col-start-3 col-span-8 flex flex-col gap-2.5">
                {products.length > 0 ? (
                  <>
                    {isPC ? <h1 className="leading-none">{collectionData?.collection.title || ""}</h1> : null}
                    <section className="pc:tnt-gap-x pc:col-span-10 pc:grid-cols-5 pc:gap-y-20 relative col-span-8 grid grid-cols-2 gap-x-4 gap-y-5">
                      {products.map(({ node }: { node: IProductDetailQuery["product"] }, index) => {
                        // 색상까지 포함해야 필터가 바뀌어도 남아있는 카드가 재사용된다 (index 키는 전체 리마운트 유발)
                        const colors = node?.options.find((item) => item.name === "Color")?.values || [];

                        return (
                          <ProductCard
                            color={colors}
                            key={`${node?.id}_${colors.join("-") || index}`}
                            releaseDate={node?.release_date}
                            tags={node?.tags}
                            title={node?.title || ""}
                            variants={node?.variants}
                          />
                        );
                      })}
                    </section>
                  </>
                ) : (
                  <>
                    {!loading ? (
                      <div className="pc:col-span-4 pc:gap-2.5 col-span-8 flex flex-col gap-2">
                        <div className="flex flex-col gap-1 leading-none">
                          <span>No results found for your search.</span>
                          <span>Try changing or clearing your filters.</span>
                        </div>
                        <Button
                          as="square"
                          className="w-fit"
                          onClick={() => {
                            setSearchParams({});
                          }}
                          variant="secondary"
                        >
                          Clear Filters
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}

                {hasNextPage ? (
                  <Button
                    as="text"
                    className="pc:col-span-12 col-span-8 my-30 flex size-full justify-center"
                    loading={loading}
                    onClick={() => {
                      void handleFetchMore();
                    }}
                  >
                    {`Load More Products (${productCount} of ${totalProducts})`}
                  </Button>
                ) : (
                  <>
                    {products.length > 20 ? (
                      <Button
                        as="text"
                        className="pc:col-span-12 col-span-8 my-30 flex size-full justify-center"
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Back to Top
                      </Button>
                    ) : null}
                  </>
                )}
              </section>
            </>
          ) : (
            <>
              {showFilter ? (
                <Filter
                  filters={collectionData?.collection.products.filters || []}
                  onClickClose={() => {
                    setShowFilter(false);
                  }}
                />
              ) : (
                <>
                  {products.length > 0 && (
                    <div
                      className="dark:bg-dj-black sticky top-0 z-20 col-span-8 flex w-full items-center justify-between gap-1 self-start bg-white py-2.5"
                      id="filter"
                    >
                      <h1 className="text-base! leading-none">{collectionData?.collection.title || ""}</h1>
                      <Button
                        as="wrapper"
                        className="h-fit"
                        onClick={() => {
                          setShowFilter(true);
                        }}
                      >
                        <Icon className="size-4.75 text-black dark:text-white" name="Filter" />
                      </Button>
                    </div>
                  )}
                </>
              )}

              <section className="pc:col-span-10 pc:grid-cols-5 pc:col-start-3 col-span-8 flex flex-col gap-2.5">
                {products.length > 0 ? (
                  <>
                    {isPC ? <h1 className="leading-none">{collectionData?.collection.title || ""}</h1> : null}
                    <section className="pc:tnt-gap-x pc:col-span-10 pc:grid-cols-5 pc:gap-y-20 relative col-span-8 grid grid-cols-2 gap-x-4 gap-y-10">
                      {products.map(({ node }: { node: IProductDetailQuery["product"] }, index) => {
                        // 색상까지 포함해야 필터가 바뀌어도 남아있는 카드가 재사용된다 (index 키는 전체 리마운트 유발)
                        const colors = node?.options.find((item) => item.name === "Color")?.values || [];

                        return (
                          <ProductCard
                            color={colors}
                            key={`${node?.id}_${colors.join("-") || index}`}
                            releaseDate={node?.release_date}
                            tags={node?.tags}
                            title={node?.title || ""}
                            variants={node?.variants}
                          />
                        );
                      })}
                    </section>
                  </>
                ) : (
                  <>
                    {!loading ? (
                      <div className="pc:col-span-4 pc:gap-2.5 col-span-8 flex flex-col gap-4">
                        <div className="flex flex-col gap-1 leading-none">
                          <span>No results found for your search.</span>
                          <span>Try changing or clearing your filters.</span>
                        </div>
                        <Button
                          as="square"
                          className="w-fit"
                          onClick={() => {
                            setSearchParams({});
                          }}
                          variant="secondary"
                        >
                          Clear Filters
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}

                {hasNextPage ? (
                  <Button
                    as="text"
                    className="pc:col-span-12 col-span-8 my-30 flex size-full justify-center"
                    loading={loading}
                    onClick={() => {
                      void handleFetchMore();
                    }}
                  >
                    {`Load More Products (${productCount} of ${totalProducts})`}
                  </Button>
                ) : (
                  <>
                    {products.length > 20 ? (
                      <Button
                        as="text"
                        className="pc:col-span-12 col-span-8 my-30 flex size-full justify-center"
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Back to Top
                      </Button>
                    ) : null}
                  </>
                )}
              </section>
            </>
          )}
        </>
      )}
    </>
  );
}
